<i18n locale="en">
{
  "unavailable": "Not available when sending to all locations.",
  "method-options": {
    "ai": "Make it with AI",
    "custom": "Make it yourself"
  },
}
</i18n>

<template>
  <div class="template-sidebar flex-col gap-8">
    <template v-if="showLocationSelector">
      <div class="flex-col gap-2">
        <OwnInputContainer :label="templateConfig.heading">
          <LocationSelector v-model="selectedLocation" />
        </OwnInputContainer>
      </div>
      <OwnRule type="dashed" />
    </template>

    <OwnTouchRadioGroup v-model="selectedMethod" :options="methodOptions" />
    <OwnRule type="dashed" />

    <div v-if="selectedMethod === 'custom'" class="flex-col gap-3">
      <OwnTouchRadioGroup
        v-model="selectedOption"
        :options="availableOptions"
      />

      <OwnCard
        v-for="option of unavailableOptions"
        :key="option.value"
        class="template-sidebar__unavailable-option"
      >
        <div class="flex-row align-center gap-3">
          <component :is="option.icon" size="24" />
          <div class="flex-col gap-1">
            <OwnType :text="option.label" variant="title-regular" />
            <OwnType :text="t('unavailable')" />
          </div>
        </div>
      </OwnCard>
    </div>

    <AiTemplateEditor
      v-else
      :location-id="selectedLocation"
      @custom="$emit('custom', $event)"
    />
  </div>
</template>

<script lang="ts">
import { PhSparkle, PhPaintBrushHousehold } from '@phosphor-icons/vue'
import { isDefined } from '@vueuse/core'
import { type Component } from 'vue'
import { markRaw } from 'vue'
import { useI18n } from 'vue-i18n'
import { mapGetters } from 'vuex'

import LocationSelector from '@/components/selectors/LocationSelector.vue'
import TitleBlock from '@/components/typography/TitleBlock.vue'
import { useApplicationScope } from '@/core/store'
import { Location } from '@/core/types'
import { OwnCard } from '@/ui/OwnCard'
import { OwnInputContainer } from '@/ui/OwnInputContainer'
import { OwnRule } from '@/ui/OwnRule'
import { OwnTouchRadioGroup } from '@/ui/OwnTouchRadioGroup'
import { OwnType } from '@/ui/OwnType'

import AiTemplateEditor from './AiTemplateEditor.vue'

interface EmailTemplateOption {
  fetchData: (brandId: string) => Promise<{ blocks: any[]; resource: any }>
  icon: Component
  label: string
  value: string
  requiresLocation?: boolean
}

interface EmailTemplateConfig {
  heading: string
  description: string
  title: string
  options: EmailTemplateOption[]
}

export default {
  name: 'TemplateSidebar',
  components: {
    AiTemplateEditor,
    LocationSelector,
    OwnCard,
    OwnInputContainer,
    OwnRule,
    OwnTouchRadioGroup,
    OwnType,
    TitleBlock,
  },
  props: {
    modelValue: { type: String, default: undefined },
    overrides: { type: Object, default: () => {} },
    previewLoaded: { type: Boolean, required: true },
    templateConfig: {
      type: Object as () => EmailTemplateConfig,
      required: true,
    },
  },
  emits: ['custom', 'update:overrides', 'update:modelValue'],
  data() {
    return {
      methodOptions: ['ai', 'custom'].map((value) => ({
        icon: markRaw(value === 'ai' ? PhSparkle : PhPaintBrushHousehold),
        label:
          this?.t?.(`method-options.${value}`) ||
          (value === 'ai' ? 'Make it with AI' : 'Make it yourself'),
        value,
      })),
      selectedLocation: undefined,
      selectedMethod: 'ai',
    }
  },
  setup() {
    const { t } = useI18n()
    const { activeLocationId } = useApplicationScope()

    return { activeLocationId, t }
  },
  computed: {
    selectedOption: {
      get() {
        return this.modelValue
      },
      set(value: string | undefined) {
        this.$emit('update:modelValue', value)
      },
    },
    ...mapGetters({
      locations: 'core/brand/active/locations',
    }),
    availableOptions() {
      const { templateConfig, selectedLocation } = this

      return templateConfig.options.filter((option: EmailTemplateOption) => {
        if (!isDefined(option.requiresLocation)) return true

        return option.requiresLocation && isDefined(selectedLocation)
      })
    },
    showLocationSelector() {
      const { locations } = this

      return locations?.length !== 1
    },
    unavailableOptions() {
      const { templateConfig, selectedLocation } = this

      return templateConfig.options.filter((option: EmailTemplateOption) => {
        if (!selectedLocation) return option.requiresLocation
        return false
      })
    },
  },
  watch: {
    previewLoaded(newPreviewLoaded) {
      if (newPreviewLoaded) {
        this.$emit('update:modelValue', undefined)
        this.$emit('custom', { blocks: [], resource: undefined })
      }
    },
    selectedLocation(newLocation) {
      this.$emit('update:overrides', { locationId: newLocation })

      const { availableOptions, unavailableOptions } = this

      // Moving from location to all locations
      if (
        !isDefined(newLocation) &&
        unavailableOptions.find(
          (option: EmailTemplateOption) => option.value === this.modelValue
        )
      ) {
        this.$emit('update:modelValue', availableOptions[0].value)
      }
    },
    selectedMethod(newMethod) {
      if (newMethod === 'ai') {
        this.$emit('update:modelValue', undefined)
        this.$emit('custom', { blocks: [], resource: undefined })
      } else {
        this.$emit('update:modelValue', 'blank')
      }
    },
  },
  created() {
    const { locations, activeLocationId } = this

    if (
      activeLocationId &&
      locations?.some((loc: Location) => loc.id === activeLocationId)
    ) {
      this.selectedLocation = activeLocationId
    } else if (locations?.length === 1) {
      // Auto-select if brand has only one location
      this.selectedLocation = locations[0].id
    }
    // Otherwise selectedLocation remains null (All Locations)
  },
}
</script>

<style lang="scss" scoped>
.template-sidebar {
  &__unavailable-option {
    padding: 16px;
    color: var(--text-color-secondary);
    cursor: not-allowed;
  }
}
</style>
